/**
 * Simplified error codes for authentication failures.
 * Provides a provider-agnostic interface for handling auth errors.
 *
 * TokenExpired is the only "expected" outcome; the remaining codes
 * categorize auth-service failures so monitors can distinguish server
 * errors, rate limiting, and other client errors instead of collapsing
 * everything into Unknown. CsrfInvalid is broken out from the generic
 * Http4xx bucket so callers can recover a stale shared-cookie CSRF 403
 * (one logout) instead of storming auth-service.
 */
export const AuthErrorCodes = {
    TokenExpired: 'token-expired',
    Http401: 'http-401',
    CsrfInvalid: 'csrf-invalid',
    Http5xx: 'http-5xx',
    RateLimited: 'rate-limited',
    Http4xx: 'http-4xx',
    Unknown: 'unknown',
};
/**
 * Provider-agnostic authentication error.
 * Can be thrown by any authentication provider.
 */
export class AuthenticationError extends Error {
    errorCode;
    statusCode;
    constructor(errorCode, statusCode, message) {
        super(message);
        this.errorCode = errorCode;
        this.statusCode = statusCode;
        this.name = 'AuthenticationError';
        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, this.constructor);
        }
    }
}
/**
 * Parse auth-service error response and return an AuthenticationError
 * @param response - The HTTP response from auth-service
 * @returns AuthenticationError with simplified error code
 */
export const parseAuthServiceError = async (response) => {
    let errorBody;
    try {
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
            errorBody = await response.json();
        }
    }
    catch {
        // If we can't parse the body, fall through to default error
    }
    const message = errorBody?.message ?? `Authentication failed: ${response.statusText}`;
    // TokenExpired requires both the 401 status AND the matching body code —
    // a token-expired body on a non-401 response is unexpected and should
    // surface as the real status so it's not masked. The specific reason for
    // a generic 401 (invalid-token, user-not-found, etc.) is preserved in
    // error.message for humans.
    // Order matters: exact-status checks (401, 429) come before range checks
    // so a 429 can't silently fall into Http4xx if the 5xx branch moves.
    let errorCode;
    if (response.status === 401 && errorBody?.errorCode === 'token-expired') {
        errorCode = AuthErrorCodes.TokenExpired;
    }
    else if (response.status === 401) {
        errorCode = AuthErrorCodes.Http401;
    }
    else if (response.status === 403 &&
        errorBody?.errorCode === 'invalid-csrf-token') {
        // The shared `.owner.com` cookie rotated under this origin's cached CSRF
        // token. Broken out so the caller recovers with a single logout instead of
        // storming auth-service; a 403 without this code stays a generic Http4xx.
        errorCode = AuthErrorCodes.CsrfInvalid;
    }
    else if (response.status === 429) {
        errorCode = AuthErrorCodes.RateLimited;
    }
    else if (response.status >= 500) {
        errorCode = AuthErrorCodes.Http5xx;
    }
    else if (response.status >= 400) {
        errorCode = AuthErrorCodes.Http4xx;
    }
    else {
        errorCode = AuthErrorCodes.Unknown;
    }
    return new AuthenticationError(errorCode, response.status, message);
};
//# sourceMappingURL=errors.js.map